/**
 * SF-4652: approve (build exactly the version on screen) and cancel (stop before anything is
 * made). On production the person types the company name first, as sf-api requires
 * (approveDemoBuild: confirm_company_name must equal change_set.company_name exactly).
 */
import { useState } from 'react';
import { Alert, Button, Card, Input, Popconfirm, Space, Typography } from 'antd';

export interface ApprovePanelProps {
  version: number;
  production: boolean;
  /** The name production asks for; null when the set has none (production cannot approve). */
  companyName: string | null;
  /** Approve is allowed now (ready for review and not waiting on Eva). */
  canApprove: boolean;
  canCancel: boolean;
  approving: boolean;
  cancelling: boolean;
  error: React.ReactNode;
  onApprove: (confirmName?: string) => void;
  onCancel: () => void;
}

export const ApprovePanel: React.FC<ApprovePanelProps> = ({
  version,
  production,
  companyName,
  canApprove,
  canCancel,
  approving,
  cancelling,
  error,
  onApprove,
  onCancel,
}) => {
  const [typed, setTyped] = useState('');
  const nameOk = !production || (companyName !== null && typed === companyName);
  return (
    <Card size="small" title="Approve" data-testid="approve-panel">
      <Space direction="vertical" style={{ width: '100%' }}>
        <Typography.Text>
          Approve builds version {version}, exactly as shown. Nothing is made until you approve.
        </Typography.Text>
        {production &&
          (companyName ? (
            <>
              <Typography.Text>
                This is production. Type <Typography.Text strong>{companyName}</Typography.Text> to
                approve.
              </Typography.Text>
              <Input
                aria-label="Company name"
                value={typed}
                onChange={(e) => setTyped(e.target.value)}
                autoComplete="off"
              />
            </>
          ) : (
            <Alert
              type="warning"
              showIcon
              message="This set has no company name to confirm, so it cannot be approved on production. Ask Eva to fix it."
            />
          ))}
        {error}
        <Space wrap>
          <Button
            type="primary"
            danger={production}
            disabled={!canApprove || !nameOk || cancelling}
            loading={approving}
            onClick={() => onApprove(production ? typed : undefined)}
          >
            Approve and build
          </Button>
          <Popconfirm
            title="Cancel this demo?"
            description="The build stops here and nothing is made."
            okText="Yes, cancel it"
            cancelText="Keep it"
            onConfirm={onCancel}
            disabled={!canCancel || approving}
          >
            <Button disabled={!canCancel || approving} loading={cancelling}>
              Cancel demo
            </Button>
          </Popconfirm>
        </Space>
      </Space>
    </Card>
  );
};
